import * as React from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { Navigate, useNavigate } from 'react-router-dom';

import PageHeader from '../components/PageHeader';
import SolarIcon from '../icons/SolarIcon';

import apiClient, { shouldRedirectToLogin } from '../services/api-client';
import AppConfig from '../services/app-config';
import { isMultiTowerEnabled, getTowers, composeUnit, parseTower, validateUnit as validateUnitShared } from '../services/unit-utils';
import CustomSnackbar from '../components/CustomSnackbar';

import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';

export default function ManualVisit({ setNavbarState, navbarState }) {
  const navigate = useNavigate();

  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
  const [loading, setLoading] = React.useState(false);
  const [rulesLoading, setRulesLoading] = React.useState(true);

  const [rules, setRules] = React.useState({
    default_lifespan_hours: 24,
    allow_multiple_entries: false,
    allow_longer_lifespan: false,
    max_lifespan_hours: 24,
    default: { walking: true, vehicle: true, allow_multiple_entries: false, default_lifespan_hours: 24, max_lifespan_hours: 24, allow_longer_lifespan: false },
    qr_confs: { qr_on_walking: true, qr_on_vehicle: true },
  });

  const [visitorName, setVisitorName] = React.useState('');
  const [walking, setWalking] = React.useState(true);
  const [vehicle, setVehicle] = React.useState(false);
  const [vehiclePlate, setVehiclePlate] = React.useState('');
  const [unit, setUnit] = React.useState('');
  const [tower, setTower] = React.useState('');
  const [apt, setApt] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [unitError, setUnitError] = React.useState('');
  const [visitType, setVisitType] = React.useState('');
  const [showAdvanceOpts, setShowAdvanceOpts] = React.useState(false);
  const [showGranular, setShowGranular] = React.useState(false);
  const [advanceOpts, setAdvanceOpts] = React.useState({ allow_multiple_entries: false, max_lifespan_hours: 24 });

  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });

  const visitorNameHasError = visitorName.trim() !== '' && visitorName.trim().split(/\s+/).length < 2;

  const unitRegex = AppConfig.get('unit_regex');
  const unitSample = AppConfig.get('unit_sample');

  const walkingEnabled = rules.walking !== false;
  const vehicleEnabled = rules.vehicle !== false;
  const forceResidentPlate = AppConfig.get('force_resident_vehicle_plate_input') === true;
  const visitTypesEnabled = AppConfig.get('visit_types_enabled') === true;
  const visitTypesList = React.useMemo(() => {
    const raw = AppConfig.get('visit_types');
    if (typeof raw === 'string' && raw.trim() !== '') {
      return raw.split(',').map((s) => s.trim()).filter(Boolean);
    }
    if (Array.isArray(raw)) return raw.filter(Boolean);
    return [];
  }, []);

  const formatTimespan = (hours) => {
    if (hours < 24) {
      return `${hours} horas`;
    } else {
      const days = Math.floor(hours / 24);
      return `${days} dia${days > 1 ? 's' : ''}`;
    }
  };

  React.useEffect(() => {
    setNavbarState({ ...navbarState, showBack: false, showOptions: false, showSearch: false });
  }, []);

  React.useEffect(() => {
    setRulesLoading(true);
    apiClient
      .get('visit/rules', {}, { action: 'fetch_visit_rules' })
      .then((response) => {
        if (response.status === 200) {
          const data = response.data.data;
          const defaultLifespanHours = data.default_lifespan_hours || 24;
          const maxHours = data.max_lifespan_hours || 24;
          const timespanOptions = [];
          const hourIncrement = defaultLifespanHours >= 12 ? 12 : (defaultLifespanHours >= 8 ? 8 : defaultLifespanHours);
          for (let h = defaultLifespanHours; h < 24 && h <= maxHours; h += hourIncrement) {
            timespanOptions.push({ value: h, label: `${h} horas` });
          }
          if (maxHours >= 24) {
            const maxDays = Math.floor(maxHours / 24);
            for (let d = 1; d <= maxDays; d++) {
              const h = d * 24;
              if (h <= maxHours) {
                timespanOptions.push({ value: h, label: `${d} dia${d > 1 ? 's' : ''}` });
              }
            }
          }
          setRules({ ...data, timespan_options: timespanOptions });
          const canShow = !!data.allow_longer_lifespan || !!data.allow_multiple_entries;
          setShowAdvanceOpts(canShow);
          setShowGranular(false);
          setAdvanceOpts({ allow_multiple_entries: false, max_lifespan_hours: defaultLifespanHours });
          const wEnabled = data.walking !== false;
          const vEnabled = data.vehicle !== false;
          if (wEnabled && !vEnabled) {
            setWalking(true);
            setVehicle(false);
          } else if (vEnabled && !wEnabled) {
            setWalking(false);
            setVehicle(true);
          } else if (!wEnabled && !vEnabled) {
            setWalking(false);
            setVehicle(false);
          }
        } else if (response.status === 401) {
          setRedirect({ trigger: true, route: '/login' });
        }
      })
      .catch((error) => {
        if (shouldRedirectToLogin(error)) {
          setRedirect({ trigger: true, route: '/login' });
        }
      })
      .finally(() => setRulesLoading(false));
  }, []);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const validateUnit = (value) => {
    if (!value.trim()) {
      setUnitError('Ingrese la unidad.');
      return false;
    }
    if (isMultiTowerEnabled()) {
      const result = validateUnitShared(value, unitRegex);
      if (!result.valid) {
        setUnitError(result.error);
        return false;
      }
      setUnitError('');
      return true;
    }
    if (unitRegex) {
      const regex = new RegExp(`^${unitRegex}$`);
      if (!regex.test(value.trim())) {
        setUnitError(`Formato inválido. Ejemplo: ${unitSample || unitRegex}`);
        return false;
      }
    }
    setUnitError('');
    return true;
  };

  const handleTowerChange = (t) => {
    setTower(t);
    const composed = composeUnit(t, apt);
    setUnit(composed);
    if (composed && unitError) validateUnit(composed);
  };

  const handleAptChange = (a) => {
    setApt(a);
    const composed = composeUnit(tower, a);
    setUnit(composed);
    if (composed && unitError) validateUnit(composed);
  };

  const handleCreate = () => {
    if (visitorName.trim() === '') {
      setSnackbar({ open: true, message: 'Ingrese el nombre del visitante.', severity: 'warning' });
      return;
    }
    if (visitorName.trim().split(/\s+/).length < 2) {
      setSnackbar({ open: true, message: 'Por favor agregar al menos un nombre y un apellido.', severity: 'warning' });
      return;
    }
    if (!walking && !vehicle) {
      setSnackbar({ open: true, message: 'Seleccione el tipo de acceso (peatonal o vehicular).', severity: 'warning' });
      return;
    }
    if (vehicle && !vehiclePlate.trim()) {
      if (forceResidentPlate) {
        setSnackbar({ open: true, message: 'Ingrese la placa del vehículo.', severity: 'warning' });
        return;
      }
    }
    if (!validateUnit(unit)) {
      return;
    }
    if (visitTypesEnabled && visitTypesList.length > 0 && !visitType) {
      setSnackbar({ open: true, message: 'Seleccione el tipo de visita.', severity: 'warning' });
      return;
    }

    const visitorNameCamelCase = visitorName
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    const data = {
      visitor_name: visitorNameCamelCase,
      walking,
      vehicle,
      vehicle_plate: vehiclePlate.trim(),
      unit: unit.trim(),
      notes: notes.trim() || null,
      max_lifespan_hours: advanceOpts.max_lifespan_hours,
      allow_multiple_entries: advanceOpts.allow_multiple_entries,
    };
    if (visitTypesEnabled && visitType) {
      data.visit_type = visitType;
    }

    setLoading(true);
    apiClient
      .post('visit/manual', data, {}, { action: 'manual_visit' })
      .then((response) => {
        if (response.status === 200) {
          setSnackbar({ open: true, message: 'Visita registrada exitosamente.', severity: 'success' });
          setTimeout(() => {
            navigate(-1);
          }, 1200);
        } else if (response.status === 401) {
          setRedirect({ trigger: true, route: '/login' });
        } else {
          setSnackbar({ open: true, message: response.data?.msg || 'Error al registrar visita.', severity: 'error' });
        }
      })
      .catch((error) => {
        const msg = error?.response?.data?.msg || 'Error al registrar visita, intente de nuevo.';
        setSnackbar({ open: true, message: msg, severity: 'error' });
      })
      .finally(() => setLoading(false));
  };

  if (redirect.trigger) {
    return <Navigate to={redirect.route} replace={true} />;
  }

  return (
    <Container maxWidth="sm" sx={{ pb: 16 }}>
      <CustomSnackbar open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={handleSnackbarClose} />

      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'background.default',
          pt: 2,
          pb: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'blur(8px)',
        }}
      >
        <PageHeader
          title="Registro Manual"
          icon="clipboard-text"
          onBack={() => navigate(-1)}
        />
      </Box>

      <Box sx={{ px: 0, pt: 3, pb: 6 }}>
        {loading || rulesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ maxWidth: 520, mx: 'auto' }}>
            <Stack spacing={2.5}>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 12, letterSpacing: '0.06em', ml: 0.5, mb: 0.75, color: 'text.secondary' }}>
                  Nombre del Visitante
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Ej. Juan Pérez"
                  value={visitorName}
                  onChange={(e) => setVisitorName(e.target.value)}
                  error={visitorNameHasError}
                  helperText={visitorNameHasError ? 'Ingrese al menos un nombre y un apellido' : ''}
                  InputProps={{ sx: { height: 56, fontWeight: 600 } }}
                />
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 12, letterSpacing: '0.06em', ml: 0.5, mb: 0.75, color: 'text.secondary' }}>
                  {AppConfig.get('unit_type') || 'Unidad'}
                </Typography>
                {isMultiTowerEnabled() ? (
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <FormControl sx={{ flex: '0 0 130px' }}>
                      <InputLabel id="manual-tower-label">Torre</InputLabel>
                      <Select
                        labelId="manual-tower-label"
                        value={tower}
                        label="Torre"
                        onChange={(e) => handleTowerChange(e.target.value)}
                        sx={{ height: 56, fontWeight: 600 }}
                      >
                        <MenuItem value="" disabled>Seleccione</MenuItem>
                        {getTowers().map((t) => (
                          <MenuItem key={t} value={t}>{t}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      placeholder="Ej. 704"
                      value={apt}
                      onChange={(e) => handleAptChange(e.target.value)}
                      error={!!unitError}
                      helperText={unitError}
                      InputProps={{ sx: { height: 56, fontWeight: 600 } }}
                    />
                  </Box>
                ) : (
                  <TextField
                    fullWidth
                    placeholder={unitSample ? `Ej. ${unitSample}` : 'Ingrese la unidad'}
                    value={unit}
                    onChange={(e) => {
                      setUnit(e.target.value);
                      if (unitError) validateUnit(e.target.value);
                    }}
                    error={!!unitError}
                    helperText={unitError}
                    InputProps={{ sx: { height: 56, fontWeight: 600 } }}
                  />
                )}
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 12, letterSpacing: '0.06em', ml: 0.5, mb: 0.75, color: 'text.secondary' }}>
                  Tipo de Acceso
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: walkingEnabled && vehicleEnabled ? '1fr 1fr' : '1fr', gap: 2 }}>
                  {walkingEnabled && (
                    <Button
                      variant={walking ? 'contained' : 'outlined'}
                      color={walking ? 'primary' : 'inherit'}
                      onClick={() => {
                        setWalking(true);
                        setVehicle(false);
                      }}
                      sx={{
                        height: 92,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                        borderWidth: 2,
                        fontWeight: 900,
                      }}
                    >
                      <SolarIcon name="users-group-rounded" size={28} />
                      Peatonal
                    </Button>
                  )}

                  {vehicleEnabled && (
                    <Button
                      variant={vehicle ? 'contained' : 'outlined'}
                      color={vehicle ? 'primary' : 'inherit'}
                      onClick={() => {
                        setVehicle(true);
                        setWalking(false);
                      }}
                      sx={{
                        height: 92,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                        borderWidth: 2,
                        fontWeight: 900,
                      }}
                    >
                      <SolarIcon name="garage" size={28} />
                      Vehicular
                    </Button>
                  )}
                </Box>
              </Box>

              {visitTypesEnabled && visitTypesList.length > 0 && (
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 12, letterSpacing: '0.06em', ml: 0.5, mb: 0.75, color: 'text.secondary' }}>
                    Tipo de Visita
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel id="manual-visit-type-label">Tipo de visita</InputLabel>
                    <Select
                      labelId="manual-visit-type-label"
                      value={visitType}
                      label="Tipo de visita"
                      onChange={(event) => setVisitType(event.target.value)}
                      sx={{ height: 56, fontWeight: 600 }}
                    >
                      <MenuItem value="" disabled>Seleccione</MenuItem>
                      {visitTypesList.map((t) => (
                        <MenuItem key={t} value={t}>{t}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {vehicle && (
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 12, letterSpacing: '0.06em', ml: 0.5, mb: 0.75, color: 'text.secondary' }}>
                    Placa del Vehículo{forceResidentPlate ? ' *' : ''}
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder={forceResidentPlate ? 'Requerido' : 'Opcional'}
                    value={vehiclePlate}
                    onChange={(e) => setVehiclePlate(e.target.value.toUpperCase())}
                    InputProps={{ sx: { height: 56, fontWeight: 600 } }}
                  />
                </Box>
              )}

              {showAdvanceOpts && (
                <Box>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => setShowGranular((prev) => !prev)}
                    sx={{
                      width: '100%',
                      height: 56,
                      justifyContent: 'space-between',
                      px: 2,
                      bgcolor: 'rgba(0,0,0,0.02)',
                      borderColor: 'rgba(0,0,0,0.06)',
                    }}
                    startIcon={<TuneIcon />}
                    endIcon={<ExpandMoreIcon sx={{ transform: showGranular ? 'rotate(180deg)' : 'none', transition: 'transform 160ms ease' }} />}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, justifyContent: 'flex-start' }}>
                      <Typography sx={{ fontWeight: 800, fontSize: 13 }}>Control Granular</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <Typography sx={{ fontWeight: 600, fontSize: 12, color: 'text.secondary', mr: 1 }}>
                        {formatTimespan(advanceOpts.max_lifespan_hours)}
                      </Typography>
                    </Box>
                  </Button>

                  <Collapse in={showGranular}>
                    <Box sx={{ pt: 2 }}>
                      <Stack spacing={2}>
                        {rules.allow_longer_lifespan && (
                          <FormControl fullWidth size="medium">
                            <InputLabel id="manual-timespan-select-label">Validez</InputLabel>
                            <Select
                              labelId="manual-timespan-select-label"
                              value={advanceOpts.max_lifespan_hours}
                              label="Validez"
                              onChange={(event) =>
                                setAdvanceOpts({
                                  allow_multiple_entries: rules.allow_multiple_entries && event.target.value !== rules.default_lifespan_hours,
                                  max_lifespan_hours: event.target.value,
                                })
                              }
                            >
                              {rules.timespan_options && rules.timespan_options.map((option, index) => (
                                <MenuItem key={index} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}

                        {advanceOpts.max_lifespan_hours === rules.default_lifespan_hours && rules.allow_multiple_entries && (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={advanceOpts.allow_multiple_entries}
                                onChange={() =>
                                  setAdvanceOpts({ ...advanceOpts, allow_multiple_entries: !advanceOpts.allow_multiple_entries })
                                }
                              />
                            }
                            label="Permitir Múltiples Accesos"
                          />
                        )}
                      </Stack>
                    </Box>
                  </Collapse>
                </Box>
              )}

              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 12, letterSpacing: '0.06em', ml: 0.5, mb: 0.75, color: 'text.secondary' }}>
                  Notas Adicionales
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Observaciones..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Box>
            </Stack>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          position: 'fixed',
          bottom: 88,
          left: 'calc(var(--app-drawer-width, 0px) + (100vw - var(--app-drawer-width, 0px)) / 2)',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: 600,
          px: 2,
          zIndex: 10,
          pointerEvents: 'none',
        }}
      >
        <Button
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          startIcon={<PersonAddAltIcon />}
          onClick={handleCreate}
          disabled={loading}
          sx={{ height: 56, fontWeight: 900, pointerEvents: 'auto' }}
        >
          Registrar Visita Manual
        </Button>
      </Box>
    </Container>
  );
}
